import { type AccountOpeningTypeSelector, type AccountType } from '../../../models/AccountOpening'
import { type FieldCitations } from '../../../models/Citation'
import {
  AccountOpeningCollections,
  type AccountOpeningCollection,
  type AccountOpeningCollectionStep,
  type AccountOpeningConfirmScope,
  type AccountOpeningItemCopy,
  type AccountOpeningPanel,
  type AccountOpeningSchemaFormPanel,
  type AccountOpeningSchemaKey,
  type AccountOpeningStep,
  type AccountOpeningStepId,
} from '../../../models/AccountOpeningDescriptor'
import { accountHeading } from '../descriptor/stepCopy'
import { applyPanelValues, hasProperties, schemaProperties, sliceSchema } from './schemaValues'
import { roleLabelIn } from './accountTypeCatalog'
import { axisMissingItems, holderConflictItems, holderMissingItems, schemaInvalidItems, schemaMissingItems, type MissingItem } from './missingFields'
import { clientContactIdOf, linkedCoHolders, partySchemaRole } from './parties'
import { accountPrefillSources, partyPrefillSources } from './prefillSources'
import { type ItemPatch, type WizardAccount, type WizardContext, type WizardHolder, type WizardParty } from './types'

interface ResolvedItemBase {
  ref: string
  heading: string
  subheading?: string
  schemaNote?: string
}

export type ResolvedItem =
  | (ResolvedItemBase & { collection: typeof AccountOpeningCollections.ACCOUNTS; account: WizardAccount })
  | (ResolvedItemBase & { collection: typeof AccountOpeningCollections.PARTIES; party: WizardParty; coHolders: WizardHolder[] })

export interface CollectionContext {
  accountTypes: AccountType[]
  selectors: AccountOpeningTypeSelector[]
  accounts: WizardAccount[]
  parties: WizardParty[]
  readOnly: boolean
  copy: Required<AccountOpeningItemCopy>
}

export const SCHEMA_KEYS: readonly AccountOpeningSchemaKey[] = ['accountTypeId', 'role']

const asSchemaKey = (value: string | undefined): AccountOpeningSchemaKey | undefined => SCHEMA_KEYS.find((key) => key === value)

export const schemaValueSource = (panel: AccountOpeningSchemaFormPanel): AccountOpeningSchemaKey | undefined =>
  panel.valueFrom !== undefined ? asSchemaKey(panel.valueFrom) : asSchemaKey(panel.keyedBy)

export const flowCollections = (steps: AccountOpeningStep[]): AccountOpeningCollection[] => [
  ...new Set(steps.flatMap((step) => (step.kind === 'collection' ? [step.collection] : []))),
]

export const collectionEntrySteps = (steps: AccountOpeningStep[]): Map<AccountOpeningCollection, AccountOpeningStepId> => {
  const entry = new Map<AccountOpeningCollection, AccountOpeningStepId>()
  steps.forEach((step) => {
    if (step.kind === 'collection' && !entry.has(step.collection)) {
      entry.set(step.collection, step.id)
    }
  })
  return entry
}

export const itemsOf = (collection: AccountOpeningCollection, ctx: CollectionContext): ResolvedItem[] => {
  if (collection === AccountOpeningCollections.PARTIES) {
    return ctx.parties.map((party) => ({
      collection: AccountOpeningCollections.PARTIES,
      ref: party.customerRefId,
      heading: party.name,
      subheading: party.roleCodes.map((code) => roleLabelIn(ctx.accountTypes, code)).join(', '),
      schemaNote:
        party.roleCodes.length > 1 ? ctx.copy.partyRoleNote.replace('{role}', roleLabelIn(ctx.accountTypes, partySchemaRole(party) ?? '')) : undefined,
      party,
      coHolders: party.contactId ? [] : linkedCoHolders(ctx.accounts, party),
    }))
  }
  return ctx.accounts.map((account, index) => ({
    collection: AccountOpeningCollections.ACCOUNTS,
    ref: account.accountRefId,
    heading: accountHeading(index, ctx.readOnly, ctx.copy),
    account,
  }))
}

export const stepItems = (step: AccountOpeningCollectionStep, ctx: CollectionContext): ResolvedItem[] => {
  const items = itemsOf(step.collection, ctx)
  return step.shared ? items.slice(0, 1) : items
}

export const formIdPrefix = (stepId: string, item: ResolvedItem, panelIndex = 0): string => `${stepId}-${item.ref}-${panelIndex}`

export const itemSkipsReview = (item: ResolvedItem): boolean => item.collection === AccountOpeningCollections.PARTIES && !!item.party.externalId

export const itemNeedsCustodianDetails = (item: ResolvedItem): boolean =>
  item.collection === AccountOpeningCollections.PARTIES && !!item.party.externalId && item.party.custodianDetailsOf !== item.party.externalId

// Existing clients are never confirmed by hand; until their custodian record is read back they would be sent empty.
export const isItemConfirmed = (item: ResolvedItem, confirmedRefs: string[]): boolean =>
  itemSkipsReview(item) ? !itemNeedsCustodianDetails(item) : confirmedRefs.includes(item.ref)

export const existingPartyPanel = (step: AccountOpeningCollectionStep): AccountOpeningSchemaFormPanel | undefined => {
  const view = step.existingParty
  const panel = (step.panels ?? []).find((candidate): candidate is AccountOpeningSchemaFormPanel => candidate.kind === 'schemaForm')
  if (!view || !panel) {
    return undefined
  }
  return { ...panel, include: view.include, exclude: view.exclude, heading: view.heading, emptyHint: undefined, sourceNote: undefined }
}

const rawSchema = (item: ResolvedItem): Record<string, unknown> =>
  item.collection === AccountOpeningCollections.ACCOUNTS ? item.account.schemaSnapshot : item.party.kycSchemaSnapshot

export const itemSchemaFetchedAt = (item: ResolvedItem): string | undefined =>
  item.collection === AccountOpeningCollections.ACCOUNTS ? item.account.schemaFetchedAt : undefined

export const itemValues = (item: ResolvedItem): Record<string, unknown> =>
  item.collection === AccountOpeningCollections.ACCOUNTS ? item.account.values : item.party.kycValues

export const itemCitations = (item: ResolvedItem): FieldCitations => (item.collection === 'accounts' ? item.account.citations : item.party.citations) ?? {}

export const panelSchema = (item: ResolvedItem, panel: AccountOpeningSchemaFormPanel): Record<string, unknown> => sliceSchema(rawSchema(item), panel)

const panelHasFields = (item: ResolvedItem, panel: AccountOpeningSchemaFormPanel): boolean => hasProperties(panelSchema(item, panel))

export const applyPanelChange = (
  item: ResolvedItem,
  panel: AccountOpeningSchemaFormPanel,
  formData: Record<string, unknown> | undefined,
): Record<string, unknown> => applyPanelValues(itemValues(item), formData, rawSchema(item), panel)

const copySharedValues = (
  step: AccountOpeningCollectionStep,
  from: Record<string, unknown>,
  to: Record<string, unknown>,
  schema: Record<string, unknown>,
): Record<string, unknown> =>
  (step.panels ?? [])
    .filter((panel): panel is AccountOpeningSchemaFormPanel => panel.kind === 'schemaForm')
    .reduce((next, panel) => applyPanelValues(next, from, schema, panel), to)

const sharedValuesFor = (step: AccountOpeningCollectionStep, values: Record<string, unknown>, target: ResolvedItem): Record<string, unknown> =>
  copySharedValues(step, values, itemValues(target), rawSchema(target))

export const sharedEdits = (
  step: AccountOpeningCollectionStep,
  item: ResolvedItem,
  patch: ItemPatch,
  ctx: CollectionContext,
): { item: ResolvedItem; patch: ItemPatch }[] => {
  const { values } = patch
  if (!step.shared || !values) {
    return []
  }
  return itemsOf(step.collection, ctx)
    .filter((other) => other.ref !== item.ref)
    .map((other) => ({ item: other, patch: { values: sharedValuesFor(step, values, other) } }))
}

const fromFirst = <T>(
  step: AccountOpeningCollectionStep,
  items: T[],
  valuesOf: (item: T) => Record<string, unknown>,
  schemaOf: (item: T) => Record<string, unknown>,
  withValues: (item: T, values: Record<string, unknown>) => T,
): T[] => {
  const [first, ...rest] = items
  if (!first) {
    return items
  }
  return [first, ...rest.map((item) => withValues(item, copySharedValues(step, valuesOf(first), valuesOf(item), schemaOf(item))))]
}

export const withSharedAnswers = (
  steps: AccountOpeningStep[],
  accounts: WizardAccount[],
  parties: WizardParty[],
): { accounts: WizardAccount[]; parties: WizardParty[] } =>
  steps
    .filter((step): step is AccountOpeningCollectionStep => step.kind === 'collection' && !!step.shared)
    .reduce(
      (current, step) =>
        step.collection === AccountOpeningCollections.ACCOUNTS
          ? {
              ...current,
              accounts: fromFirst(
                step,
                current.accounts,
                (account) => account.values,
                (account) => account.schemaSnapshot,
                (account, values) => ({ ...account, values }),
              ),
            }
          : {
              ...current,
              parties: fromFirst(
                step,
                current.parties,
                (party) => party.kycValues,
                (party) => party.kycSchemaSnapshot,
                (party, kycValues) => ({ ...party, kycValues }),
              ),
            },
      { accounts, parties },
    )

const panelMissingItems = (item: ResolvedItem, panel: AccountOpeningPanel, ctx: CollectionContext): MissingItem[] => {
  switch (panel.kind) {
    case 'typeSummary':
      return item.collection === AccountOpeningCollections.ACCOUNTS ? axisMissingItems(ctx.selectors, item.account) : []
    case 'holders':
      return item.collection === AccountOpeningCollections.ACCOUNTS
        ? [...holderMissingItems(ctx.accountTypes, item.account), ...holderConflictItems(item.account)]
        : []
    case 'schemaForm':
      return schemaMissingItems(panelSchema(item, panel), itemValues(item), `${item.collection}:${item.ref}`)
  }
}

export const itemMissingItems = (step: AccountOpeningCollectionStep, item: ResolvedItem, ctx: CollectionContext): MissingItem[] => {
  if (itemSkipsReview(item)) {
    return []
  }
  const items = (step.panels ?? []).flatMap((panel) => panelMissingItems(item, panel, ctx))
  if (item.collection === AccountOpeningCollections.PARTIES && !item.party.name.trim()) {
    return [{ key: 'name', label: ctx.copy.partyNameLabel }, ...items]
  }
  return items
}

const isItemComplete = (step: AccountOpeningCollectionStep, item: ResolvedItem, ctx: CollectionContext): boolean =>
  itemMissingItems(step, item, ctx).length === 0

export interface ItemGroup {
  key: string
  heading: string
  items: MissingItem[]
}

const groupBy = (step: AccountOpeningCollectionStep, ctx: CollectionContext, itemsOfGroup: (item: ResolvedItem) => MissingItem[]): ItemGroup[] =>
  stepItems(step, ctx)
    .map((item) => ({ key: item.ref, heading: item.heading, items: itemsOfGroup(item) }))
    .filter((group) => group.items.length > 0)

export const stepMissingGroups = (step: AccountOpeningCollectionStep, ctx: CollectionContext): ItemGroup[] =>
  groupBy(step, ctx, (item) => itemMissingItems(step, item, ctx))

const itemInvalidItems = (step: AccountOpeningCollectionStep, item: ResolvedItem): MissingItem[] =>
  itemSkipsReview(item)
    ? []
    : (step.panels ?? []).flatMap((panel) =>
        panel.kind === 'schemaForm' ? schemaInvalidItems(panelSchema(item, panel), itemValues(item), `${item.collection}:${item.ref}`) : [],
      )

export const stepInvalidGroups = (step: AccountOpeningCollectionStep, ctx: CollectionContext): ItemGroup[] =>
  groupBy(step, ctx, (item) => itemInvalidItems(step, item))

export const isStepComplete = (step: AccountOpeningCollectionStep, ctx: CollectionContext): boolean => {
  const items = stepItems(step, ctx)
  return items.length > 0 && items.every((item) => isItemComplete(step, item, ctx))
}

const prefillSchema = (step: AccountOpeningCollectionStep, item: ResolvedItem): Record<string, unknown> => {
  const panel = (step.panels ?? []).find((candidate): candidate is AccountOpeningSchemaFormPanel => candidate.kind === 'schemaForm')
  return panel ? panelSchema(item, panel) : rawSchema(item)
}

export const itemFillable = (step: AccountOpeningCollectionStep, item: ResolvedItem): boolean =>
  !itemSkipsReview(item) && (step.panels ?? []).some((panel) => panel.kind === 'schemaForm' && panelHasFields(item, panel))

export const prefillWanted = (step: AccountOpeningCollectionStep, item: ResolvedItem): boolean => {
  if (itemSkipsReview(item)) {
    return false
  }
  const asked = Object.keys(schemaProperties(prefillSchema(step, item)))
  return asked.length > 0 && !asked.some((name) => itemValues(item)[name] !== undefined)
}

export const prefillSourcesFor = (item: ResolvedItem, ctx: WizardContext, step: AccountOpeningCollectionStep): Record<string, unknown> => {
  const schema = prefillSchema(step, item)
  return item.collection === AccountOpeningCollections.ACCOUNTS
    ? accountPrefillSources(ctx, item.account, schema)
    : partyPrefillSources(ctx, item.party, schema, clientContactIdOf(item.coHolders, step.prefill?.clientRoles ?? []))
}

export const collectionSchemaSource = (
  steps: AccountOpeningStep[],
  collection: AccountOpeningCollection,
): { entity: string; keyedBy: AccountOpeningSchemaFormPanel['keyedBy']; valueFrom: ReturnType<typeof schemaValueSource> } | undefined => {
  for (const step of steps) {
    if (step.kind !== 'collection' || step.collection !== collection) {
      continue
    }
    const panel = (step.panels ?? []).find((candidate): candidate is AccountOpeningSchemaFormPanel => candidate.kind === 'schemaForm')
    if (panel) {
      return { entity: panel.entity, keyedBy: panel.keyedBy, valueFrom: schemaValueSource(panel) }
    }
  }
  return undefined
}

export const stepsKeyedBy = (
  steps: AccountOpeningStep[],
  collection: AccountOpeningCollection,
  valueFrom: ReturnType<typeof schemaValueSource>,
): AccountOpeningStepId[] =>
  steps
    .filter(
      (step) =>
        step.kind === 'collection' &&
        step.collection === collection &&
        (step.panels ?? []).some((panel) => panel.kind === 'schemaForm' && schemaValueSource(panel) === valueFrom),
    )
    .map((step) => step.id)

export const confirmScopes = (
  steps: AccountOpeningStep[],
  item: ResolvedItem,
  patch: ItemPatch,
  stepId: AccountOpeningStepId,
): Record<AccountOpeningStepId, AccountOpeningConfirmScope | undefined> => {
  const affected = new Set<AccountOpeningStepId>([stepId])
  if (patch.typeId !== undefined) {
    stepsKeyedBy(steps, item.collection, 'accountTypeId').forEach((keyed) => affected.add(keyed))
  }
  return Object.fromEntries(
    [...affected].map((affectedId) => {
      const step = steps.find((candidate) => candidate.id === affectedId)
      return [affectedId, step?.kind === 'collection' ? step.confirm?.scope : undefined]
    }),
  )
}

export const itemSchemaKey = (item: ResolvedItem, valueFrom: ReturnType<typeof schemaValueSource>): string => {
  switch (valueFrom) {
    case 'accountTypeId':
      return item.collection === AccountOpeningCollections.ACCOUNTS ? item.account.typeId : ''
    case 'role':
      return item.collection === AccountOpeningCollections.PARTIES ? (partySchemaRole(item.party) ?? '') : ''
    default:
      return ''
  }
}

export const itemNeedsSchema = (item: ResolvedItem, valueFrom: ReturnType<typeof schemaValueSource>): boolean =>
  !!itemSchemaKey(item, valueFrom) && Object.keys(rawSchema(item)).length === 0

export const preparationSignature = (steps: AccountOpeningStep[], collection: AccountOpeningCollection, ctx: CollectionContext): string => {
  const source = collectionSchemaSource(steps, collection)
  if (!source) {
    return ''
  }
  const items = itemsOf(collection, ctx)
  return [
    ...items.filter((item) => itemNeedsSchema(item, source.valueFrom)).map((item) => `${item.ref}:${itemSchemaKey(item, source.valueFrom)}`),
    ...items
      .filter(itemNeedsCustodianDetails)
      .map((item) => `${item.ref}:details:${item.collection === AccountOpeningCollections.PARTIES ? item.party.externalId : ''}`),
  ]
    .sort()
    .join('|')
}
